/**
 * `crypto.randomUUID()` with a graceful fallback for older WebViews.
 *
 * ROOT CAUSE THIS FIXES (PostHog issue 019fc4f3, 2026-08-03)
 * ----------------------------------------------------------
 * `TypeError: crypto.randomUUID is not a function` on **Chrome 90 / Android**,
 * origin `https://localhost` — i.e. an old Android System WebView inside the
 * installed APK, on `/dashboard/profile`. `crypto.randomUUID` only shipped in
 * Chrome 92 (July 2021); `crypto.getRandomValues` has been available since
 * Chrome 11. So the device is perfectly capable of generating a strong random
 * id — it just lacks the newer convenience wrapper.
 *
 * This matters well beyond the one reported component. The same unguarded call
 * sat on the **synthetic-email signup path** (`SignupPage`, `UserContext`),
 * where a throw means the user cannot create an account at all, and on every
 * document/photo upload filename. Those are silent, total failures on the exact
 * low-end devices this platform targets.
 *
 * WHY THE FALLBACK IS NOT A DOWNGRADE: it draws from `crypto.getRandomValues`
 * and assembles a spec-shaped RFC 4122 v4 UUID (version + variant bits set), so
 * the randomness quality is identical to the native implementation. The
 * `Math.random` branch is a genuine last resort for an environment with no Web
 * Crypto at all, and is deliberately NEVER reachable for anything security
 * bearing — these ids are storage filenames, Google Places session tokens and
 * React list keys. Per CLAUDE.md §5 item 17, OTPs and auth tokens are generated
 * server-side with `crypto.getRandomValues` + rejection sampling and must never
 * be routed through here.
 */

function getWebCrypto(): Crypto | undefined {
  const c = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  return typeof c?.getRandomValues === "function" ? c : undefined;
}

/** RFC 4122 v4 layout from 16 random bytes. */
function formatUuidV4(bytes: Uint8Array): string {
  // Version 4 in the high nibble of byte 6, variant 10xx in byte 8.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push(bytes[i].toString(16).padStart(2, "0"));

  return (
    hex.slice(0, 4).join("") +
    "-" +
    hex.slice(4, 6).join("") +
    "-" +
    hex.slice(6, 8).join("") +
    "-" +
    hex.slice(8, 10).join("") +
    "-" +
    hex.slice(10, 16).join("")
  );
}

/**
 * Returns an RFC 4122 v4 UUID string. Never throws.
 *
 * Prefers the native `crypto.randomUUID()`, falls back to `getRandomValues`,
 * and only then to `Math.random` (no Web Crypto at all — not security bearing,
 * see the file header).
 */
export function safeRandomUUID(): string {
  const c = getWebCrypto();

  // Native path. Guarded by a try as well as a typeof check: `randomUUID` is
  // additionally gated on a secure context, and a non-secure origin can expose
  // the property while throwing on call.
  if (typeof c?.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
      /* fall through to getRandomValues */
    }
  }

  if (c) {
    return formatUuidV4(c.getRandomValues(new Uint8Array(16)));
  }

  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  return formatUuidV4(bytes);
}

export default safeRandomUUID;
